import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { resolveSupportChannelUrl } from '~/services/modules/support/resolveSupportChannelUrl';

const SUPPORT_SECTOR = 'tecnologia';

type JourneySupportDrawerProps = {
  appId: string;
  open: boolean;
  onClose: () => void;
};

/**
 * Drawer com o iframe do canal de atendimento (chatbot). A URL vem em runtime de
 * `GET /v2/supportSector?sector=tecnologia`.
 *
 * Extraído de `JourneyHelpButton` para ser reutilizado pelo passo `not_recognized`
 * da jornada, que no Figma direciona «Falar com suporte» para o mesmo chatbot.
 */
export default function JourneySupportDrawer({ appId, open, onClose }: JourneySupportDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [channelUrl, setChannelUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  const loadChannel = useCallback(async () => {
    setLoading(true);
    setHasError(false);
    try {
      // import dinâmico: evita puxar o cliente axios (`~/services/api`, que usa
      // `import.meta`) no grafo de módulos até o usuário abrir o atendimento.
      const { getSupportSector } = await import('~/services/modules/support');
      const payload = await getSupportSector(SUPPORT_SECTOR);
      const url = resolveSupportChannelUrl(payload);
      if (url) {
        setChannelUrl(url);
      } else {
        setHasError(true);
      }
    } catch (error) {
      const { reportError } = await import('~/utils/reportError');
      reportError('Erro ao carregar canal de atendimento', error as Error, { context: 'JourneySupportDrawer' });
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !channelUrl && !loading) void loadChannel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Drawer anchor="right" data-app-id={appId} onClose={onClose} open={open}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: { sm: 420, xs: '100vw' } }}>
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: 'grey.900',
            // Header sempre escuro: texto branco nos dois modos (`primary.contrastText` no light não é branco).
            color: 'common.white',
            display: 'flex',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5
          }}
        >
          {/* O tema fixa `color: text.primary` nas variantes de Typography; no light isso dava texto escuro no header. */}
          <Typography
            data-app-id={`${appId}-title`}
            sx={{ color: 'common.white', fontWeight: 600 }}
            variant="subtitle1"
          >
            Atendimento
          </Typography>
          <IconButton
            aria-label="Fechar atendimento"
            data-app-id={`${appId}-close`}
            onClick={onClose}
            sx={{ color: 'inherit' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {loading ? (
            <Box sx={{ alignItems: 'center', display: 'flex', height: '100%', justifyContent: 'center' }}>
              <CircularProgress data-app-id={`${appId}-loading`} />
            </Box>
          ) : hasError || !channelUrl ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography sx={{ mb: 2 }} variant="body1">
                Não foi possível carregar o atendimento agora. Tente novamente.
              </Typography>
              <Button data-app-id={`${appId}-retry`} onClick={() => void loadChannel()} variant="contained">
                Tentar novamente
              </Button>
            </Box>
          ) : (
            <Box
              component="iframe"
              data-app-id={`${appId}-iframe`}
              src={channelUrl}
              title="Atendimento Aride"
              // O index.html tem `iframe { display: none }` global; forçamos display:block via sx.
              sx={{ border: 0, display: 'block', height: '100%', width: '100%' }}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
